import ActionMenu from "./ActionMenu";
import React, { useEffect, useState } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import bgVideo from "../assets/bg.mp4";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/passwordPolicy";

function ProfileModal({ tenant, onClose, onViewDoc }) {
  if (!tenant) return null;

  const documents = [
    { label: "Barangay Clearance", value: tenant.barangay_clearance },
    { label: "Valid ID", value: tenant.valid_id },
    { label: "Proof of Income", value: tenant.proof_of_income || tenant.contract },
  ].filter((doc) => doc.value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">
              Tenant Profile
            </p>
            <h2 className="mt-2 text-2xl font-bold">{tenant.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Email
            </p>
            <p className="mt-1 break-words font-semibold text-white">
              {tenant.email}
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Contact
            </p>
            <p className="mt-1 font-semibold text-white">
              {tenant.contact_number || "Not provided"}
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-4 sm:col-span-2">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
              Address
            </p>
            <p className="mt-1 font-semibold text-white">
              {tenant.address || "Not provided"}
            </p>
          </div>
        </div>

        <div className="mt-5">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
            Documents
          </h3>

          {documents.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-3">
              {documents.map((doc) => (
                <button
                  key={doc.label}
                  onClick={() => onViewDoc(doc.value)}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-left text-sm font-semibold text-slate-100 transition hover:border-teal-300/50 hover:bg-teal-500/10"
                >
                  {doc.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-white/15 px-4 py-6 text-center text-sm text-slate-300">
              No documents uploaded.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageModal({ show, onClose, imageSrc }) {
  if (!show) return null;
  const isPdf =
    typeof imageSrc === "string" &&
    imageSrc.trim().toLowerCase().startsWith("data:application/pdf");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-lg border border-white/10 bg-slate-950 p-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg bg-slate-950/80 px-3 py-1 text-sm font-semibold text-white transition hover:bg-slate-900"
        >
          X
        </button>

        {isPdf ? (
          <iframe
            src={imageSrc}
            title="Document Preview"
            className="h-[82vh] w-full rounded bg-white"
          />
        ) : (
          <img
            src={imageSrc}
            alt="Document"
            className="max-h-[82vh] w-full rounded object-contain"
          />
        )}
      </div>
    </div>
  );
}

function EditModal({ tenant, onClose, onSave, setTenant }) {
  if (!tenant) return null;

  const inputClass =
    "w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">
              Tenant Details
            </p>
            <h3 className="mt-2 text-2xl font-bold">Edit Tenant</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>

        <div className="space-y-3">
          <input
            className={inputClass}
            placeholder="Full Name"
            value={tenant.name}
            onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
          />

          <input
            className={inputClass}
            placeholder="Email"
            value={tenant.email}
            onChange={(e) => setTenant({ ...tenant, email: e.target.value })}
          />

          <input
            className={inputClass}
            placeholder="Contact Number"
            value={tenant.contact_number || ""}
            onChange={(e) =>
              setTenant({ ...tenant, contact_number: e.target.value })
            }
          />

          <input
            className={inputClass}
            placeholder="Address"
            value={tenant.address || ""}
            onChange={(e) => setTenant({ ...tenant, address: e.target.value })}
          />

          <input
            type="password"
            className={inputClass}
            placeholder="New Password"
            value={tenant.password || ""}
            onChange={(e) => setTenant({ ...tenant, password: e.target.value })}
          />
          <p className="text-xs text-slate-400">{PASSWORD_POLICY_MESSAGE}</p>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ["Barangay Clearance", "barangay_clearance_file"],
              ["Valid ID", "valid_id_file"],
              ["Contract", "contract_file"],
            ].map(([label, key]) => (
              <label
                key={key}
                className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-slate-300"
              >
                <p className="mb-2 font-semibold uppercase tracking-[0.14em] text-slate-400">
                  {label}
                </p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) =>
                    setTenant({
                      ...tenant,
                      [key]: e.target.files?.[0] || null,
                    })
                  }
                  className="block w-full text-[11px] text-slate-300 file:mr-2 file:rounded-md file:border file:border-white/20 file:bg-slate-900 file:px-2 file:py-1 file:text-[11px] file:font-semibold file:text-slate-200 hover:file:bg-slate-800"
                />
                <p className="mt-2 truncate text-[11px] text-teal-200">
                  {tenant[key]?.name || "No new file selected"}
                </p>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-5 py-2 font-semibold text-slate-200 transition hover:bg-white/10"
          >
            Cancel
          </button>

          <button
            onClick={() => onSave(tenant)}
            className="rounded-lg bg-teal-600 px-5 py-2 font-semibold text-white transition hover:bg-teal-500"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TenantList() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [imageModal, setImageModal] = useState({ show: false, data: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState(null);
  const [undoTenantId, setUndoTenantId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    const res = await api.get("/admin/tenants");
    setTenants(res.data?.data || []);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this tenant?")) return;

    const category = window
      .prompt(
        "Archive category (Resolved, Duplicate, Invalid, Policy, Other)",
        "Resolved"
      )
      ?.trim();
    if (!category) return;

    const allowed = ["Resolved", "Duplicate", "Invalid", "Policy", "Other"];
    if (!allowed.includes(category)) {
      setToast({ message: "Invalid archive category.", type: "error" });
      return;
    }

    const note = window.prompt("Archive note", "Tenant record no longer active")?.trim();
    if (!note || note.length < 3) {
      setToast({ message: "Archive note is required.", type: "error" });
      return;
    }

    try {
      await api.delete(`/admin/tenants/${id}`, {
        data: {
          archive_reason_category: category,
          archive_reason_note: note,
        },
      });
      setTenants((prev) => prev.filter((t) => t.id !== id));
      setUndoTenantId(id);
      setTimeout(() => setUndoTenantId(null), 8000);
      setToast({ message: "Tenant archived. Undo available for 8 seconds.", type: "success" });
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to archive tenant.", type: "error" });
    }
  };

  const handleUndoArchive = async () => {
    if (!undoTenantId) return;
    try {
      await api.put(`/admin/tenants/${undoTenantId}/restore`);
      setUndoTenantId(null);
      fetchTenants();
      setToast({ message: "Archive undone. Tenant restored.", type: "success" });
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to undo archive.", type: "error" });
    }
  };

  const handleUpdateTenant = async (tenantData) => {
    const password = tenantData.password?.trim() || "";

    if (password && !isStrongPassword(password)) {
      setToast({ message: PASSWORD_POLICY_MESSAGE, type: "error" });
      return;
    }

    const form = new FormData();
    form.append("name", tenantData.name || "");
    form.append("email", tenantData.email || "");
    form.append("contact_number", tenantData.contact_number || "");
    form.append("address", tenantData.address || "");

    if (password) {
      form.append("password", password);
    }

    if (tenantData.barangay_clearance_file instanceof File) {
      form.append("barangay_clearance", tenantData.barangay_clearance_file);
    }

    if (tenantData.valid_id_file instanceof File) {
      form.append("valid_id", tenantData.valid_id_file);
    }

    if (tenantData.contract_file instanceof File) {
      form.append("contract", tenantData.contract_file);
    }

    if (!password) {
      // keep backend password unchanged
    }

    try {
      const res = await api.post(`/admin/tenants/${tenantData.id}?_method=PUT`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updatedTenant = res.data?.data || tenantData;

      setTenants((prev) =>
        prev.map((t) => (t.id === tenantData.id ? updatedTenant : t))
      );
      setEditingTenant(null);
      setToast({ message: "Tenant updated successfully.", type: "success" });
    } catch (err) {
      console.error(err);
      const errors = err.response?.data?.errors;
      const message =
        errors?.password?.[0] ||
        errors?.email?.[0] ||
        errors?.name?.[0] ||
        err.response?.data?.message ||
        "Tenant update failed.";

      setToast({ message, type: "error" });
    }
  };

  const filteredTenants = tenants.filter((tenant) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    return [tenant.name, tenant.email, tenant.contact_number, tenant.address]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true"
          className="h-full w-full object-cover"
          src={bgVideo}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-slate-950/85"></div>
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 text-white sm:px-6 lg:px-8">
        <ProfileModal
          tenant={selectedTenant}
          onClose={() => setSelectedTenant(null)}
          onViewDoc={(img) => setImageModal({ show: true, data: img })}
        />

        <EditModal
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onSave={handleUpdateTenant}
          setTenant={setEditingTenant}
        />

        <ImageModal
          show={imageModal.show}
          imageSrc={imageModal.data}
          onClose={() => setImageModal({ show: false, data: null })}
        />

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            <div className="flex items-center gap-4">
              <span>{toast.message}</span>
              {undoTenantId && (
                <button
                  onClick={handleUndoArchive}
                  className="rounded border border-white/30 px-2 py-1 text-xs font-semibold hover:bg-white/10"
                >
                  Undo
                </button>
              )}
              <button
                onClick={() => setToast(null)}
                className="text-white/80 hover:text-white"
              >
                X
              </button>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Tenants
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide text-white md:text-4xl">
              Tenant Management
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Review tenant profiles, update account details, and open uploaded
              documents from one workspace.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-center sm:min-w-[260px]">
            <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
              <p className="text-2xl font-bold text-white">{tenants.length}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Tenants
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
              <p className="text-2xl font-bold text-teal-200">
                {filteredTenants.length}
              </p>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Showing
              </p>
            </div>
          </div>
        </div>

        <div className="mb-5 flex flex-col gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl md:flex-row md:items-center md:justify-between">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 md:max-w-md"
            placeholder="Search tenants by name, email, contact, or address"
          />

          <button
            onClick={() => navigate("/admin/tenants/add")}
            className="rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-500"
          >
            Add Tenant
          </button>
        </div>

        <div className="hidden overflow-x-auto rounded-lg border border-white/10 bg-slate-950/90 shadow-2xl md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-[0.18em] text-slate-400">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Address</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredTenants.length > 0 ? (
                filteredTenants.map((t) => (
                  <tr
                    key={t.id}
                    className="cursor-pointer border-b border-white/10 transition last:border-b-0 hover:bg-white/5"
                  >
                    <td className="px-5 py-4 font-semibold text-white">
                      <button type="button" className="record-name" onClick={() => setSelectedTenant(t)}>{t.name}</button>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300">
                      {t.email}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300">
                      {t.contact_number || "Not provided"}
                    </td>
                    <td className="max-w-sm px-5 py-4 text-sm text-slate-300">
                      <p className="line-clamp-2">
                        {t.address || "Not provided"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <ActionMenu context={t.name}><button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTenant({ ...t });
                          }}
                          className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                        >
                          Edit
                        </button>

                        <button data-danger="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(t.id);
                          }}
                          className="rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                        >
                          Archive
                        </button></ActionMenu>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-5 py-10 text-center text-slate-300">
                    No tenants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-4 md:hidden">
          {filteredTenants.length > 0 ? (
            filteredTenants.map((t) => (
              <div
                key={t.id}
                className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl transition hover:border-teal-300/30"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-white"><button type="button" className="record-name" onClick={() => setSelectedTenant(t)}>{t.name}</button></h3>
                    <p className="mt-1 break-words text-sm text-slate-300">
                      {t.email}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">
                      {t.contact_number || "No contact number"}
                    </p>
                  </div>

                  <ActionMenu context={t.name}><button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTenant({ ...t });
                      }}
                      className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white"
                    >
                      Edit
                    </button>

                    <button data-danger="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(t.id);
                      }}
                      className="rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100"
                    >
                      Archive
                    </button></ActionMenu>
                </div>

                <p className="mt-4 rounded-lg bg-white/5 p-3 text-sm text-slate-300">
                  {t.address || "No address provided"}
                </p>
              </div>
            ))
          ) : (
            <p className="rounded-lg border border-dashed border-white/15 py-8 text-center text-slate-300">
              No tenants found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
